import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { UseMutationOptions, useQueryClient } from 'react-query';
import type { RestaurantAccountManager } from './types';
import { getCmsRestaurantsEndpointIdentifier } from './use-get-cms-restaurants-api';

const endpoint = 'api/v1/restaurants';

type TData = RestaurantAccountManager;
type TError = AxiosError;
type TVariables = { restaurantId: number; userId: number };

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useAssignAccountManagerApi(options?: Options): TReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ restaurantId, userId }: TVariables): Promise<TData> => {
    try {
      const { data } = await http.put(`${endpoint}/${restaurantId}/account-manager`, { userId });
      return data;
    } catch (error: any) {
      toast({
        description: error.response?.data?.message || 'Failed to assign the account manager',
        status: 'error',
      });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${endpoint}/account-manager`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([getCmsRestaurantsEndpointIdentifier]);
      toast({ description: 'Account manager updated', status: 'success' });
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
